<template>
  <transition name="modal-fade">
    <div v-if="visible" class="modal-mask" @click.self="handleClose">
      <div
        class="modal-panel"
        :class="{ 'is-detail': !!detail }"
        role="dialog"
        aria-modal="true"
      >
        <div class="panel-header">
          <template v-if="detail">
            <button type="button" class="back-btn" @click="detail = null">
              <i class="bi bi-arrow-left"></i> 返回列表
            </button>
            <h2 class="detail-header-title">{{ detail.title }}</h2>
          </template>
          <h2 v-else>消息通知</h2>
          <button type="button" class="close-btn" aria-label="关闭" @click="handleClose">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div v-if="!detail" class="panel-body list-body">
          <div v-if="loading" class="state-box">加载中...</div>
          <div v-else-if="!articles.length" class="state-box">暂无公告</div>
          <div v-else class="article-list">
            <button
              v-for="item in articles"
              :key="item.articleId"
              type="button"
              class="article-item"
              :class="{ unread: !isRead(item.articleId) }"
              @click="openDetail(item)"
            >
              <span v-if="!isRead(item.articleId)" class="dot"></span>
              <div v-if="item.coverUrl" class="item-cover-wrap">
                <img class="item-cover" :src="item.coverUrl" alt="" />
              </div>
              <div v-else class="item-cover-placeholder">
                <i class="bi bi-image"></i>
              </div>
              <div class="item-main">
                <div class="item-title">{{ item.title }}</div>
                <div class="item-summary">{{ item.summary || '点击查看详情' }}</div>
                <div class="item-time">{{ formatTime(item.publishTime || item.createTime) }}</div>
              </div>
              <i class="bi bi-chevron-right item-arrow"></i>
            </button>
          </div>
        </div>

        <div v-else class="panel-body detail-body">
          <div class="detail-meta">{{ formatTime(detail.publishTime || detail.createTime) }}</div>
          <div class="detail-content rich-content" v-html="detail.content"></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { listArticles, getArticle } from '@/api/portal/article'
import { markArticleRead, getReadArticleIds } from '@/utils/articleRead'

export default {
  name: 'ArticleNotifyModal',
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      articles: [],
      detail: null,
      readIds: []
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.detail = null
        this.loadArticles()
      }
    }
  },
  methods: {
    handleClose() {
      this.$emit('close')
      this.$emit('read-change')
    },
    loadArticles() {
      this.loading = true
      this.readIds = getReadArticleIds()
      listArticles()
        .then(res => {
          this.articles = res.data || []
        })
        .catch(() => {
          this.articles = []
        })
        .finally(() => {
          this.loading = false
        })
    },
    isRead(articleId) {
      return this.readIds.includes(String(articleId))
    },
    openDetail(item) {
      getArticle(item.articleId)
        .then(res => {
          this.detail = res.data
          markArticleRead(item.articleId)
          this.readIds = getReadArticleIds()
          this.$emit('read-change')
        })
        .catch(err => {
          alert(err.message || '加载失败')
        })
    },
    formatTime(val) {
      if (!val) return ''
      return String(val).replace('T', ' ').substring(0, 16)
    }
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
}

.modal-panel {
  position: relative;
  width: min(640px, 100%);
  max-height: min(72vh, 680px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
  transition: width 0.2s ease, max-height 0.2s ease;
}

.modal-panel.is-detail {
  width: min(920px, 96vw);
  max-height: min(90vh, 900px);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef0f4;
  flex-shrink: 0;
}

.panel-header h2 {
  margin: 0;
  font-size: 18px;
  flex: 1;
  min-width: 0;
}

.detail-header-title {
  font-size: 17px;
  font-weight: 600;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 10px;
  margin-right: 4px;
  border-radius: 8px;
  color: var(--primary, #1976d2);
  font-size: 14px;
}

.back-btn:hover {
  background: #f3f4f6;
}

.close-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: #6b7280;
  flex-shrink: 0;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.list-body {
  min-height: 280px;
}

.detail-body {
  overflow: auto;
  padding: 20px 28px 32px;
}

.state-box {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: #9ca3af;
}

.article-list {
  flex: 1;
  overflow: auto;
  padding: 8px 12px 12px;
}

.article-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  text-align: left;
}

.article-item:hover {
  background: #f9fafb;
}

.article-item.unread .item-title {
  font-weight: 600;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  flex-shrink: 0;
  margin-left: -2px;
}

.item-cover-wrap,
.item-cover-placeholder {
  width: 72px;
  height: 54px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
}

.item-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-cover-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d1d5db;
  font-size: 22px;
}

.item-main {
  flex: 1;
  min-width: 0;
}

.item-arrow {
  flex-shrink: 0;
  color: #d1d5db;
  font-size: 14px;
}

.item-title {
  font-size: 14px;
  color: #111827;
  margin-bottom: 4px;
}

.item-summary {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-time {
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.detail-meta {
  margin-bottom: 20px;
  font-size: 13px;
  color: #9ca3af;
}

.detail-content {
  font-size: 15px;
  line-height: 1.85;
  color: #374151;
  word-break: break-word;
}

.detail-content >>> p {
  margin: 0 0 1em;
}

.detail-content >>> h1,
.detail-content >>> h2,
.detail-content >>> h3 {
  margin: 1.2em 0 0.6em;
  line-height: 1.4;
  color: #111827;
}

.detail-content >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 12px 0;
}

.detail-content >>> ul,
.detail-content >>> ol {
  padding-left: 1.4em;
  margin: 0 0 1em;
}

.detail-content >>> table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
}

.detail-content >>> th,
.detail-content >>> td {
  border: 1px solid #d1d5db;
  padding: 8px 12px;
  vertical-align: top;
}

.detail-content >>> th {
  background: #f3f4f6;
  font-weight: 600;
}

@media (max-width: 640px) {
  .modal-mask {
    padding: 12px;
  }

  .modal-panel.is-detail {
    width: 100%;
    max-height: 92vh;
  }

  .detail-body {
    padding: 16px 18px 24px;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
